// src/services/importacaoFinanceiraService.js
import { pb } from "./pocketbase";
import { logAction } from "./logService";
import { financeiroService } from "./financeiroService";

export const importacaoFinanceiraService = {
  // ─── FILA DE CONFERÊNCIA (PENDÊNCIAS) ─────────────────────────────────
  async getPendenciasPaginated({ page = 1, perPage = 20, status = "pendente", loteId = null }) {
    const filterParts = [];
    if (status) {
      filterParts.push(`status = "${status}"`);
    }
    if (loteId) {
      filterParts.push(`lote_importacao = "${loteId}"`);
    }
    const filterString = filterParts.join(" && ");

    return await pb.collection("fin_lancamentos_pendentes").getList(page, perPage, {
      sort: "created", // Mais antigas primeiro
      filter: filterString || null,
      expand: "categoria_sugerida,centro_custo_sugerido,lote_importacao",
    });
  },

  async confirmarPendencia(id, currentUser) {
    const pendencia = await pb.collection("fin_lancamentos_pendentes").getOne(id);

    if (pendencia.status !== "pendente") {
      throw new Error("Esta pendência já foi resolvida.");
    }

    const isConciliacao = pendencia.origem === "conciliacao_bancaria";

    // 1. Cria o lançamento no ledger real (Invariante I3)
    const lancamento = await pb.collection("fin_lancamentos").create({
      data: pendencia.data_sugerida,
      centro_custo: pendencia.centro_custo_sugerido,
      categoria: pendencia.categoria_sugerida,
      descricao: pendencia.descricao_sugerida,
      valor_centavos: pendencia.valor_sugerido_centavos,
      status: "pago", // Padrão ao confirmar pendência operacional
      tipo_lancamento: pendencia.tipo_pendencia === "estorno" ? "estorno" : "normal",
      origem: pendencia.origem,
      origem_id: pendencia.origem_id,
      pendencia_origem: id,
      transacao_bancaria: isConciliacao ? pendencia.origem_id : null,
      valor_conciliado_centavos: isConciliacao ? pendencia.valor_sugerido_centavos : null,
    });

    // Se for conciliação, atualiza a transação do extrato bancário
    if (isConciliacao && pendencia.origem_id) {
      try {
        await pb.collection("fin_extrato_transacoes").update(pendencia.origem_id, {
          status: "conciliado",
          lancamento_vinculado: lancamento.id,
          conciliado_por: currentUser.id,
          data_conciliacao: new Date().toISOString(),
        });
        
        // Também incrementa o contador de conciliações no lote do extrato
        const trans = await pb.collection("fin_extrato_transacoes").getOne(pendencia.origem_id);
        if (trans.lote) {
          const loteExt = await pb.collection("fin_extratos_importados").getOne(trans.lote);
          await pb.collection("fin_extratos_importados").update(trans.lote, {
            total_conciliadas: (loteExt.total_conciliadas || 0) + 1,
          });
        }
      } catch (err) {
        console.warn("Erro ao atualizar transação bancária após confirmação de pendência:", err.message);
      }
    }

    // 2. Atualiza a pendência staging
    const res = await pb.collection("fin_lancamentos_pendentes").update(id, {
      status: "confirmado",
      revisado_por: currentUser.id,
      data_revisao: new Date().toISOString(),
      lancamento_gerado: lancamento.id,
    });

    // 3. Incrementa contador no lote de importação
    if (pendencia.lote_importacao) {
      try {
        const lote = await pb.collection("fin_importacoes").getOne(pendencia.lote_importacao);
        await pb.collection("fin_importacoes").update(pendencia.lote_importacao, {
          total_confirmadas: (lote.total_confirmadas || 0) + 1,
        });
      } catch (err) {
        console.warn("Erro ao atualizar total do lote de importação:", err);
      }
    }

    await financeiroService.registerSummaryForCreate(lancamento);
    if (lancamento.status === "pago") {
      await financeiroService.triggerFinanciamentoUpdate(lancamento, currentUser);
    }

    await logAction(currentUser, "FINANCEIRO", "PENDENCIA_CONFIRMADA", `Confirmou pendência ID: ${id}. Gerou lançamento ID: ${lancamento.id}`, {
      pendenciaId: id,
      lancamentoId: lancamento.id,
    });

    return res;
  },

  async editarEConfirmarPendencia(id, editedData, currentUser) {
    const pendencia = await pb.collection("fin_lancamentos_pendentes").getOne(id);

    if (pendencia.status !== "pendente") {
      throw new Error("Esta pendência já foi resolvida.");
    }

    const isConciliacao = pendencia.origem === "conciliacao_bancaria";

    // 1. Cria o lançamento no ledger real com dados modificados
    const lancamento = await pb.collection("fin_lancamentos").create({
      data: editedData.data,
      centro_custo: editedData.centro_custo,
      categoria: editedData.categoria,
      descricao: editedData.descricao,
      valor_centavos: editedData.valor_centavos,
      status: editedData.status || "pago",
      tipo_lancamento: pendencia.tipo_pendencia === "estorno" ? "estorno" : "normal",
      origem: pendencia.origem,
      origem_id: pendencia.origem_id,
      pendencia_origem: id,
      observacoes: editedData.observacoes,
      conta_bancaria: editedData.conta_bancaria,
      forma_pagamento: editedData.forma_pagamento,
      transacao_bancaria: isConciliacao ? pendencia.origem_id : null,
      valor_conciliado_centavos: isConciliacao ? editedData.valor_centavos : null,
    });

    // Se for conciliação, atualiza a transação do extrato
    if (isConciliacao && pendencia.origem_id) {
      try {
        await pb.collection("fin_extrato_transacoes").update(pendencia.origem_id, {
          status: "conciliado",
          lancamento_vinculado: lancamento.id,
          conciliado_por: currentUser.id,
          data_conciliacao: new Date().toISOString(),
        });

        const trans = await pb.collection("fin_extrato_transacoes").getOne(pendencia.origem_id);
        if (trans.lote) {
          const loteExt = await pb.collection("fin_extratos_importados").getOne(trans.lote);
          await pb.collection("fin_extratos_importados").update(trans.lote, {
            total_conciliadas: (loteExt.total_conciliadas || 0) + 1,
          });
        }
      } catch (err) {
        console.warn("Erro ao atualizar transação bancária após confirmação/edição de pendência:", err.message);
      }
    }

    // 2. Atualiza a pendência staging
    const res = await pb.collection("fin_lancamentos_pendentes").update(id, {
      status: "editado_e_confirmado",
      revisado_por: currentUser.id,
      data_revisao: new Date().toISOString(),
      lancamento_gerado: lancamento.id,
    });

    // 3. Incrementa contador no lote de importação
    if (pendencia.lote_importacao) {
      try {
        const lote = await pb.collection("fin_importacoes").getOne(pendencia.lote_importacao);
        await pb.collection("fin_importacoes").update(pendencia.lote_importacao, {
          total_confirmadas: (lote.total_confirmadas || 0) + 1,
        });
      } catch (err) {
        console.warn("Erro ao atualizar total do lote de importação:", err);
      }
    }

    await financeiroService.registerSummaryForCreate(lancamento);
    if (lancamento.status === "pago") {
      await financeiroService.triggerFinanciamentoUpdate(lancamento, currentUser);
    }

    await logAction(currentUser, "FINANCEIRO", "PENDENCIA_EDITADA_E_CONFIRMADA", `Editou e confirmou pendência ID: ${id}. Gerou lançamento ID: ${lancamento.id}`, {
      pendenciaId: id,
      lancamentoId: lancamento.id,
      updates: editedData,
    });

    return res;
  },

  async rejeitarPendencia(id, motivo, currentUser) {
    const pendencia = await pb.collection("fin_lancamentos_pendentes").getOne(id);

    if (pendencia.status !== "pendente") {
      throw new Error("Esta pendência já foi resolvida.");
    }

    // Invariante I6: Rejeição não apaga. Salva status e motivo.
    const res = await pb.collection("fin_lancamentos_pendentes").update(id, {
      status: "rejeitado",
      revisado_por: currentUser.id,
      data_revisao: new Date().toISOString(),
      motivo_rejeicao: motivo, // Campo dinâmico de texto
    });

    // Incrementa contador de rejeitadas no lote
    if (pendencia.lote_importacao) {
      try {
        const lote = await pb.collection("fin_importacoes").getOne(pendencia.lote_importacao);
        await pb.collection("fin_importacoes").update(pendencia.lote_importacao, {
          total_rejeitadas: (lote.total_rejeitadas || 0) + 1,
        });
      } catch (err) {
        console.warn("Erro ao atualizar total do lote de importação:", err);
      }
    }

    await logAction(currentUser, "FINANCEIRO", "PENDENCIA_REJEITADA", `Rejeitou pendência ID: ${id}. Motivo: ${motivo}`, {
      pendenciaId: id,
      motivo,
    });

    return res;
  },

  // ─── IMPORTAÇÃO DE LOTES DE PLANILHAS ─────────────────────────────────
  async createLoteImportacao(importacaoData, currentUser) {
    const res = await pb.collection("fin_importacoes").create({
      ...importacaoData,
      importado_por: currentUser?.id || null,
      status: "processando",
      total_confirmadas: 0,
      total_rejeitadas: 0,
    });
    return res;
  },

  async finalizeLoteImportacao(loteId, status = "aguardando_conferencia") {
    return await pb.collection("fin_importacoes").update(loteId, { status });
  },

  async createPendenciasBulk(pendencias, loteId) {
    // Processamento chunked para evitar concorrência e database lock no SQLite/PocketBase
    const chunkSize = 10;
    const results = [];

    for (let i = 0; i < pendencias.length; i += chunkSize) {
      const chunk = pendencias.slice(i, i + chunkSize);
      
      const chunkResults = await Promise.all(
        chunk.map((pendencia) => {
          return pb.collection("fin_lancamentos_pendentes").create({
            ...pendencia,
            lote_importacao: loteId,
            status: "pendente",
          });
        })
      );

      results.push(...chunkResults);
    }

    return results;
  },

  async getImportacoesRecentes() {
    return await pb.collection("fin_importacoes").getFullList({
      sort: "-created",
      limit: 10,
      expand: "importado_por",
    });
  },
};
